import { createContext, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
const Ctx = createContext<{ pref: ThemePref; resolved: 'light' | 'dark'; setPref: (p: ThemePref) => void } | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => {
    try { return (localStorage.getItem('bf_theme') as ThemePref) || 'system'; } catch { return 'system'; }
  });
  const [sysDark, setSysDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const f = (e: MediaQueryListEvent) => setSysDark(e.matches);
    mq.addEventListener('change', f);
    return () => mq.removeEventListener('change', f);
  }, []);
  const resolved = pref === 'system' ? (sysDark ? 'dark' : 'light') : pref;
  // Layout effect: set the theme before first paint so the right logo and colours show immediately.
  useLayoutEffect(() => { document.documentElement.dataset.theme = resolved; }, [resolved]);
  const setPref = (p: ThemePref) => { setPrefState(p); try { localStorage.setItem('bf_theme', p); } catch { /* ignore */ } };
  return <Ctx.Provider value={{ pref, resolved, setPref }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTheme outside provider');
  return c;
}

/** White-label: apply a workspace brand colour to --brand tokens at runtime. */
export function applyBrand(color?: string | null) {
  const root = document.documentElement.style;
  if (color && /^#[0-9a-f]{6}$/i.test(color)) {
    root.setProperty('--brand', color);
    root.setProperty('--brand-hover', `color-mix(in srgb, ${color} 88%, black)`);
    root.setProperty('--brand-press', `color-mix(in srgb, ${color} 76%, black)`);
    root.setProperty('--brand-soft', `color-mix(in srgb, ${color} 10%, transparent)`);
    // Pick the label colour with the better WCAG contrast against the workspace's brand colour.
    root.setProperty('--brand-contrast', contrastWithWhite(color) >= 4.5 ? '#ffffff' : '#14162b');
  } else {
    ['--brand', '--brand-hover', '--brand-press', '--brand-soft', '--brand-contrast'].forEach((p) => root.removeProperty(p));
  }
}

function luminance(hex: string) {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
}
export function contrastWithWhite(hex: string) {
  return 1.05 / (luminance(hex) + 0.05);
}
